import React, { useState, useMemo, useEffect, useCallback, useRef, ReactElement } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { useQueryClient } from '@tanstack/react-query';
import { PieChartData } from '@Types/nivo';
import Notification from '@Common/Notification';
import {
    useLoadLocationsByProjects,
    useGetOverviewTab,
    DashboardSetting,
    OverviewLocationFilters,
    SortByValue,
    SortDirValue,
    OVERVIEW_TAB_QUERY_KEY,
    LOCATION_BREAKDOWN_QUERY_KEY,
    BREAKDOWN_TASKS_QUERY_KEY,
} from '@Apis/companyDashboard';
import { useLoadLocationDetails } from '@Apis/locationDashboard';
import HeaderTile from './Tiles/HeaderTile';
import ByAttributeTile from './Tiles/ByAttributeTile';
import { LocationDetailData, LocationSummaryData, LOCATION_SORT_FIELDS } from '../types';
import LocationsList from './LocationsList';
import { BreakdownSelection } from './breakdownSelection';
import LocationDetailTile, { DEFAULT_LOCATION_DETAIL_TAB_ID, TASKS_LOCATION_DETAIL_TAB_ID } from './Tiles/LocationDetailTile';
import LocationDetailBreadcrumb from './Tiles/LocationDetailBreadcrumb';
import './Openings.scss';

interface OpeningsProps {
    /** Which configurable Overview tab this is — scopes both the data fetch and the breakdown panel. */
    tabId: number;
    projectIds: number[];
    selectedSectionIds?: string[];
    dashboardSettings?: DashboardSetting;
    /** Opens the page's filters drawer (the same one the toolbar's "Filters" button opens). */
    onOpenTaskFilters?: () => void;
    onSortChange: (sortBy: SortByValue, sortDir: SortDirValue) => void;
}

const Openings = ({ tabId, projectIds, selectedSectionIds = [], dashboardSettings, onOpenTaskFilters, onSortChange }: OpeningsProps): ReactElement => {
    // When selectedSectionIds is empty (legacy config or API not yet returning the field),
    // default to showing all 3 existing sections so the tab is never blank.
    const showAll = selectedSectionIds.length === 0;
    const show = (id: string) => showAll || selectedSectionIds.includes(id);

    // Locations Summary and Locations Summary Expandable are one widget with the breakdown
    // caret switched on or off, so they render as a single section. A tab that selects both
    // gets the expandable one: two copies of the list would fight over the shared detail
    // sidebar, and the expandable version is a superset of what the plain one shows.
    const showLocationsSummary = show('locationsSummary') || show('locationsSummaryExpandable');
    const isLocationsSummaryExpandable = show('locationsSummaryExpandable');

    const [selectedLocationKey, setSelectedLocationKey] = useState<string | null>(null);
    const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);

    // How far down the list the selected row sits; the inline detail card is pushed down by it
    // so it starts level with the location it describes instead of at the top of the list.
    const [activeRowOffset, setActiveRowOffset] = useState(0);

    // Non-null only while the sidebar is showing a group the user drilled into from a
    // breakdown panel. It is what puts the breadcrumb and the back arrow on the sidebar, so
    // every path that selects a plain location row has to clear it.
    const [breakdownSelection, setBreakdownSelection] = useState<BreakdownSelection | null>(null);

    // Held here rather than inside the tile, the same way LocationsList owns the breakdown
    // panel's view: picking a different location keeps the sub-tab the user was reading, and
    // the inline tile and the mobile drawer stay on the same one.
    const [detailTabId, setDetailTabId] = useState<number>(DEFAULT_LOCATION_DETAIL_TAB_ID);

    // Read by the drill-down callbacks, which must not take `detailTabId` as a dependency:
    // they are handed to LocationsList, and a fresh identity on every tab change would
    // re-render every row in the list.
    const detailTabIdRef = useRef(detailTabId);
    useEffect(() => { detailTabIdRef.current = detailTabId; }, [detailTabId]);

    // Non-null only while drilled in: the tab that was open before the drill-down forced the
    // sidebar onto Tasks. Restored on the way out, so a drill-down cannot strand the user on
    // a tab that has nothing to show outside one.
    const preDrillTabIdRef = useRef<number | null>(null);

    const locationFilters: OverviewLocationFilters = useMemo(() => ({
        projectIds: dashboardSettings?.projectIds?.length ? dashboardSettings.projectIds : projectIds,
        brandIds: dashboardSettings?.brandIds ?? [],
        areaIds: dashboardSettings?.areaIds ?? [],
        regionIds: dashboardSettings?.regionIds ?? [],
        managementCompanyIds: dashboardSettings?.managementCompanyIds ?? [],
        locationTypeIds: dashboardSettings?.locationTypeIds ?? [],
        continentIds: dashboardSettings?.continentIds ?? [],
        locationIds: dashboardSettings?.locationIds ?? [],
        tagIds: dashboardSettings?.tagIds ?? [],
        disciplineIds: dashboardSettings?.disciplineIds ?? [],
        departmentIds: dashboardSettings?.departmentIds ?? [],
        communityIds: dashboardSettings?.communityIds ?? [],
        phaseIds: dashboardSettings?.phaseIds ?? [],
        baselineIds: dashboardSettings?.baselineIds ?? [],
        isMilestonesOnly: dashboardSettings?.isMilestonesOnly ?? false,
    }), [projectIds, dashboardSettings]);

    // ---- Pie chart data: locations by projects ----
    const { data: locationsByProjectsData, error: locationsByProjectsError } = useLoadLocationsByProjects(locationFilters);

    const openingsByBrand = (locationsByProjectsData?.brand ?? []) as unknown as PieChartData[];
    const openingsByArea = (locationsByProjectsData?.area ?? []) as unknown as PieChartData[];
    const openingsByRegion = (locationsByProjectsData?.region ?? []) as unknown as PieChartData[];
    const openingsByType = (locationsByProjectsData?.type ?? []) as unknown as PieChartData[];
    const openingsByMgmtCo = (locationsByProjectsData?.managementCompany ?? []) as unknown as PieChartData[];

    // ---- Locations list from overview tab (includes metrics, filtered by dashboard settings) ----
    const sortBy = dashboardSettings?.sortBy ?? 'name';
    const sortDir = dashboardSettings?.sortDir ?? 'asc';
    // Describe the active direction in the sort field's own terms (e.g. name → "A to Z",
    // zero date → "Earliest first") for the direction toggle's tooltip and aria-label.
    const sortField = LOCATION_SORT_FIELDS.find((field) => field.value === sortBy);
    const sortDirectionLabel = sortDir === 'asc'
        ? sortField?.ascLabel ?? 'Ascending'
        : sortField?.descLabel ?? 'Descending';
    // `isLoading`, never `isFetching`: this feeds a `hideWhileLoading` indicator in the tile,
    // and the query key is the serialized filters, so `isFetching` goes true again on every
    // filter tweak and on refetch-on-mount — which would blank the graph out of every
    // visible row mid-scroll. `isLoading` is only true while there is nothing to show yet.
    const {
        data: overviewTabData,
        error: overviewTabError,
        isLoading: isLoadingMetrics,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isFetchNextPageError,
    } = useGetOverviewTab(tabId, locationFilters, { sortBy, sortDir });

    // Memoized so the array identity is stable across renders — LocationsList defaults the
    // selection to the first item whenever the `locations` reference changes, so an unstable
    // reference would snap the selection back to the first location on every re-render.
    // Metrics ride along the response via schema passthrough, so cast to the richer type.
    // Locations with no tasks in scope are already dropped by the API, which has to know them
    // before it can page the list. Each page is a separate request, so a location whose position
    // moved between two of them can arrive twice; the list keys rows by id, so keep the first.
    const locations = useMemo(() => {
        const seen = new Set<number>();
        return (overviewTabData?.pages ?? [])
            .flatMap((page) => page.locations as unknown as LocationSummaryData[])
            .filter((location) => {
                if (seen.has(location.id)) return false;
                seen.add(location.id);
                return true;
            });
    }, [overviewTabData]);

    // cancelRefetch: false keeps a scroll burst from cancelling and re-issuing the page in flight.
    const loadMoreLocations = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) void fetchNextPage({ cancelRefetch: false });
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Assigning tasks moves them out of a tile's Unassigned count and changes who the breakdown
    // panel and its drill-down list show as responsible, so all three are refetched.
    const queryClient = useQueryClient();
    const onAssignTasksComplete = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: [OVERVIEW_TAB_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [LOCATION_BREAKDOWN_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [BREAKDOWN_TASKS_QUERY_KEY] });
    }, [queryClient]);

    // ---- Location detail (keyed by selected locationKey) ----
    const { data: locationDetailsData, error: locationDetailsError } = useLoadLocationDetails(selectedLocationKey);

    // Surface load failures to the user. The old Redux path showed a Notification on each
    // failure; the react-query hooks expose errors via `error`, so mirror that here.
    useEffect(() => {
        if (locationsByProjectsError) {
            Notification('error', 'Error', 'An error occurred while loading location attribute data.');
        }
    }, [locationsByProjectsError]);

    // A failed next page leaves every loaded row on screen and puts a Retry under the list, so
    // it gets no toast — that would read as the whole list having failed to load.
    useEffect(() => {
        if (overviewTabError && !isFetchNextPageError) {
            Notification('error', 'Error', 'An error occurred while loading openings data.');
        }
    }, [overviewTabError, isFetchNextPageError]);

    useEffect(() => {
        if (locationDetailsError) {
            Notification('error', 'Error', 'An error occurred while loading location details.');
        }
    }, [locationDetailsError]);

    // Build the activeLocationDetail by merging server response with the selected locationKey.
    // The details-filters endpoint does not echo back locationKey, so we inject it here.
    const activeLocationDetail: LocationDetailData | null =
        locationDetailsData && selectedLocationKey
            ? ({ ...locationDetailsData.location, locationKey: selectedLocationKey } as LocationDetailData)
            : null;

    // Every way out of a drill-down runs through here: the breadcrumb's back arrow, the
    // header's close, and picking any row in the list.
    const leaveDrillDown = useCallback(() => {
        if (preDrillTabIdRef.current === null) return;
        setDetailTabId(preDrillTabIdRef.current);
        preDrillTabIdRef.current = null;
        setBreakdownSelection(null);
    }, []);

    // Drilling into a group on a row that is not the selected one starts a fresh details
    // request, and `useLoadLocationDetails` has no placeholder data, so `activeLocationDetail`
    // is null until it resolves. The tile cannot render without a location, but the breadcrumb
    // can — and it carries the back arrow, so without this the drill-down would be unescapable
    // for the length of the request.
    const isLoadingDrillDown = breakdownSelection !== null && activeLocationDetail === null;

    // Memoized so LocationsList's effect can safely depend on it without re-running on every render.
    const onLocationChange = useCallback((id: number) => {
        leaveDrillDown();
        const location = locations.find(loc => loc.id === id);
        if (!location) {
            setSelectedLocationKey(null);
            return;
        }
        setSelectedLocationKey(location.locationKey);
        setIsDetailDrawerOpen(true);
    }, [locations, leaveDrillDown]);

    // Drilling into a breakdown row is a request to see that group's tasks, so it opens the
    // sidebar on the Tasks tab regardless of which tab was last remembered. The displaced tab
    // is stashed rather than overwritten: outside a drill-down the Tasks tab has no group to
    // scope to, so leaving the user parked on it would both strand them on an empty panel and
    // lose the tab they were reading.
    const onBreakdownGroupSelect = useCallback((selection: BreakdownSelection) => {
        if (preDrillTabIdRef.current === null) {
            // Tasks is a normal entry in the tab strip, so it can already be the open tab when
            // the drill starts. Stashing it would hand the user straight back to a panel that
            // reads "No tasks selected" the moment they leave the drill-down.
            preDrillTabIdRef.current = detailTabIdRef.current === TASKS_LOCATION_DETAIL_TAB_ID
                ? DEFAULT_LOCATION_DETAIL_TAB_ID
                : detailTabIdRef.current;
        }
        setBreakdownSelection(selection);
        setSelectedLocationKey(selection.locationKey);
        setDetailTabId(TASKS_LOCATION_DETAIL_TAB_ID);
        setIsDetailDrawerOpen(true);
    }, []);

    // The back arrow / close button on a drilled-in sidebar. It leaves the drill-down, not the
    // location: LocationsList owns the row highlight and gets no reset signal, so dropping the
    // selection here would leave the sidebar reading "Select a location" under a row that is
    // still visibly selected. The sidebar falls back to the plain location detail instead, and
    // the breakdown panel — also owned by LocationsList — stays expanded behind it.
    const onBreakdownBack = useCallback(() => {
        leaveDrillDown();
        setIsDetailDrawerOpen(false);
    }, [leaveDrillDown]);

    return (
        <div className='overview-openings'>
            {show('recentAndUpcomingOpenings') && (
                <div className='row g-3'>
                    <div className='col-md-6 col-lg-2'>
                        <HeaderTile title='Opened in past 90 days' text={'56'}></HeaderTile>
                    </div>
                    <div className='col-md-12 col-lg-6 col-xl-6'>
                        <HeaderTile title='Openings by Month'></HeaderTile>
                    </div>
                    <div className='col-md-6 col-lg-4'>
                        <HeaderTile title='Milestones Completed'></HeaderTile>
                    </div>
                </div>
            )}

            {show('locationsBreakdownByAttribute') && (
                <div className='row g-3'>
                    <div className='col-sm-12 section-header section-header'>Number of Location/Teams by Attribute</div>

                    <div className='col-sm-12 by-attribute-tiles'>
                        <ByAttributeTile title='Brand' data={openingsByBrand} />
                        <ByAttributeTile title='Area' data={openingsByArea} />
                        <ByAttributeTile title='Region' data={openingsByRegion} />
                        <ByAttributeTile title='Type' data={openingsByType} />
                        <ByAttributeTile title='Mgmt Co' data={openingsByMgmtCo} />
                    </div>
                </div>
            )}

            {showLocationsSummary && (
                <>
                    <div className='row'>
                        <div className='col-sm-12 section-header locations-section-header'>
                            <span>Locations</span>
                            <div className='locations-sort-controls'>
                                <label className='locations-sort-label' htmlFor='locations-sort-field'>Sort by</label>
                                <Dropdown
                                    inputId='locations-sort-field'
                                    className='locations-sort-dropdown'
                                    value={sortBy}
                                    options={LOCATION_SORT_FIELDS}
                                    optionLabel='label'
                                    optionValue='value'
                                    optionDisabled='disabled'
                                    onChange={(e) => onSortChange(e.value, sortDir)}
                                    aria-label='Sort locations by'
                                />
                                <button
                                    type='button'
                                    className='locations-sort-direction'
                                    disabled={sortField?.disabled}
                                    title={sortDirectionLabel}
                                    aria-label={`Toggle sort direction (currently ${sortDirectionLabel})`}
                                    onClick={() => onSortChange(sortBy, sortDir === 'asc' ? 'desc' : 'asc')}>
                                    <i className={`pi ${sortDir === 'asc' ? 'pi-sort-amount-up-alt' : 'pi-sort-amount-down'}`} />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className='row g-3'>
                        <div className='col-12 col-lg-8'>
                            <LocationsList
                                locations={locations}
                                onLocationChange={onLocationChange}
                                onBreakdownGroupSelect={onBreakdownGroupSelect}
                                isLoadingMetrics={isLoadingMetrics}
                                tabId={tabId}
                                filters={locationFilters}
                                onOpenFilters={onOpenTaskFilters}
                                isExpandable={isLocationsSummaryExpandable}
                                hasMore={!!hasNextPage}
                                isLoadingMore={isFetchingNextPage}
                                onLoadMore={loadMoreLocations}
                                hasLoadMoreError={isFetchNextPageError}
                                onActiveRowOffsetChange={setActiveRowOffset}
                                onAssignTasksComplete={onAssignTasksComplete}
                            ></LocationsList>
                        </div>

                        {/* Inline detail panel — hidden on mobile (shown as a drawer instead) */}
                        <div
                            className='col-12 col-lg-4 location-detail-inline'
                            style={{ '--location-detail-offset': `${activeRowOffset}px` } as React.CSSProperties}>
                            {activeLocationDetail && (
                                <LocationDetailTile
                                    location={activeLocationDetail}
                                    projectIds={locationFilters.projectIds}
                                    activeTabId={detailTabId}
                                    onTabChange={setDetailTabId}
                                    breakdownSelection={breakdownSelection}
                                    onBack={onBreakdownBack}
                                    tabId={tabId}
                                    filters={locationFilters} />
                            )}
                            {isLoadingDrillDown && breakdownSelection && (
                                <div className='location-detail-tile'>
                                    <LocationDetailBreadcrumb
                                        selection={breakdownSelection}
                                        current='group'
                                        onBack={onBreakdownBack}
                                    />
                                    <div className='location-detail-placeholder'>Loading location details…</div>
                                </div>
                            )}
                            {!activeLocationDetail && !isLoadingDrillDown && (
                                <div className='location-detail-placeholder'>Select a location to view details</div>
                            )}
                        </div>
                    </div>

                    {/* Mobile-only slide-in drawer for the location detail. Also opens while a
                        drill-down's details request is in flight so the user gets immediate
                        feedback and a back control instead of a drawer that never appears. */}
                    <div
                        className={`location-detail-backdrop ${isDetailDrawerOpen && (activeLocationDetail || isLoadingDrillDown) ? 'is-open' : ''}`}
                        onClick={() => setIsDetailDrawerOpen(false)}
                    />
                    <div className={`location-detail-drawer ${isDetailDrawerOpen && (activeLocationDetail || isLoadingDrillDown) ? 'is-open' : ''}`}>
                        <div className='location-detail-drawer-header'>
                            <button
                                type='button'
                                className='location-detail-drawer-close'
                                aria-label='Close location details'
                                onClick={() => setIsDetailDrawerOpen(false)}>
                                <i className='fas fa-xmark'></i>
                            </button>
                        </div>
                        <div className='location-detail-drawer-body'>
                            {activeLocationDetail && (
                                <LocationDetailTile
                                    location={activeLocationDetail}
                                    projectIds={locationFilters.projectIds}
                                    activeTabId={detailTabId}
                                    onTabChange={setDetailTabId}
                                    breakdownSelection={breakdownSelection}
                                    onBack={onBreakdownBack}
                                    tabId={tabId}
                                    filters={locationFilters} />
                            )}
                            {isLoadingDrillDown && breakdownSelection && (
                                <div className='location-detail-tile'>
                                    <LocationDetailBreadcrumb
                                        selection={breakdownSelection}
                                        current='group'
                                        onBack={onBreakdownBack}
                                    />
                                    <div className='location-detail-placeholder'>Loading location details…</div>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Openings;
